import { ComponentProps, useRef } from 'react';
import { FeedCard as SharedFeedCard } from '@socialchorus/shared-ui-components';
import useInview from '../../../common/use-inview';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { useLinkNavigation } from '../../../common/use-link-navigation';
import { contentSelectors } from '../../../models/content';
import {
  trackContentCardClick,
  trackContentCardView,
} from '../../../models/content/analytics';
import {
  getContentAuthorAvatarSrc,
  useContentAuthorLink,
} from '../../../models/content/helpers';
import { Content } from '../../../models/content/types';
import { Feature, getFeatureFlag } from '../../../models/features/features';
import { useCustomHomepageTraits } from '../../../screens/home/custom-homepage-traits';

type SolarFeedCardDataOptions = {
  onClick?: () => void;
};

/**
 * Data + analytics derivation shared by every Solar feed card composition —
 * the full legacy card and the trimmed mobile-minimal cards alike: the
 * content URL (UGC archived/preview aware), title/description read from the
 * selectors so a translated value is picked up rather than the feed-query
 * prop (see FeedCardWithData's title/description gotcha), the card image,
 * author info, and click/view analytics wiring. Composition — which
 * sub-components to render — is left to the caller.
 *
 * The returned `analyticsData` is the caller's traits with the custom-homepage
 * attribution merged in; every event the card raises — including the ones its
 * children raise — must use it, or that event loses the attribution.
 */
export function useSolarFeedCardData(
  content: Content,
  analyticsData: IAnalyticsData,
  opts: SolarFeedCardDataOptions = {}
) {
  const cardAnalyticsData = useCustomHomepageTraits(analyticsData);
  const getLinkNavigation = useLinkNavigation();

  const baseContentUrl = usePatronSelector((state) =>
    contentSelectors.getContentDetailUrl(state, { contentId: content.id })
  );
  const ugcEnabled = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.UGC_ENABLED)
  );
  const isArchivedUgc = usePatronSelector(
    (state) =>
      ugcEnabled &&
      contentSelectors.getContentIsArchivedUgcV2(state, {
        contentId: content.id,
      })
  );
  const needsPreview = usePatronSelector(
    (state) =>
      ugcEnabled &&
      contentSelectors.getContentIsNonPublishedUgcV2(state, {
        contentId: content.id,
      })
  );
  const contentUrl = isArchivedUgc
    ? undefined
    : needsPreview
    ? `${baseContentUrl}/preview`
    : baseContentUrl;

  const title = usePatronSelector((state) =>
    contentSelectors.getContentTitle(state, { contentId: content.id })
  );
  const description = usePatronSelector((state) =>
    contentSelectors.getContentSummary(state, { contentId: content.id })
  );

  const avatarSrc = getContentAuthorAvatarSrc(content);
  const authorHref = useContentAuthorLink(content);
  const contentAuthorLink = authorHref
    ? getLinkNavigation(authorHref)
    : undefined;

  const contentImageUrl =
    content.feed_preview_image_urls[0] || content.background_image_url;
  const cardImage: ComponentProps<typeof SharedFeedCard.Image>['image'] =
    content.content_type === 'note'
      ? { type: 'note-icon', color: content.border_color }
      : { url: contentImageUrl, alt: content.source_image_alt };

  const ref = useRef<HTMLElement>(null);
  useInview({
    ref,
    once: true,
    onInview: () => trackContentCardView(content.id, cardAnalyticsData),
  });

  const handleClick = () => {
    trackContentCardClick(content.id, cardAnalyticsData);
    opts.onClick?.();
  };

  return {
    ref,
    href: contentUrl ? getLinkNavigation(contentUrl) : undefined,
    onClick: handleClick,
    analyticsData: cardAnalyticsData,
    title,
    description,
    cardImage,
    avatarSrc,
    authorName: content.source_name,
    contentAuthorLink,
    getLinkNavigation,
  };
}
